import React, { type FC } from 'react';

import { mqMin, variant } from '@lucid/theme';
import styled from '@emotion/styled';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import NavLink from './nav-link';
import Heading from '../heading/heading';
import type { NavDropdownMenuLinkInterface } from './nav-dropdown-menu';

type NavDropdownMenuGroupProps = {
  link: NavDropdownMenuLinkInterface;
  isGroupSplit?: boolean;
  index: number;
  isSuperNav?: boolean;
  isDropdownOpen?: boolean;
  product: Lucid.Product.Sites;
  className?: string;
};

const StyledNavLabel = styled(Heading)(
  variant({
    scale: 'nav.label',
    prop: 'type',
    variants: {
      defaultVariants: {},
    },
  }),
  {
    margin: 0,
    padding: 0,
    width: 'calc(100% - 24px)',
  },
);

const renderDropdownLink = ({
  link,
  index,
  isSuperNav,
  isDropdownOpen,
  product,
}: {
  link: Lucid.Drupal.LinkInterface;
  index: number;
  isSuperNav?: boolean;
  isDropdownOpen?: boolean;
  product: Lucid.Product.Sites;
}) => {
  const isProductSuite = link.entityBundle === 'product-suite';
  const isTopNavigation = link.entityBundle === 'top-navigation';

  if ((isProductSuite || isTopNavigation) && link.isEnabled === false) {
    // use '=== false' instead of '!link.enabled'
    // since this is used across all sites, err on the side of rendering the item
    // in case "enabled" is removed from the query
    return null;
  }

  return (
    <li
      key={`navDropdownMenuGroupLink-${link.entityId}-${index + 1}`}
      css={[
        {
          label: 'navDropdownMenuGroupLinkContainer',
          display: 'flex',
          alignItems: 'center',
          flex: '0 1 100%',
          maxWidth: '100%',
        },
      ]}
      tabIndex={-1}
    >
      <NavLink
        isInDropdown
        isSuperNav={isSuperNav}
        link={link}
        product={product}
        shouldRemoveFromTabIndex={!isDropdownOpen}
        variant="subNavLink"
      />
    </li>
  );
};

const NavDropdownMenuGroup: FC<NavDropdownMenuGroupProps> = ({
  link,
  isGroupSplit,
  index,
  isSuperNav,
  isDropdownOpen,
  product,
  className,
}) => (
  <Box
    css={theme => [
      {
        label: 'navDropdownMenuGroup',
        justifyContent: 'space-between',
        flex: '0 1 100%',
        width: 'calc(100% + 24px)',
        minWidth: '210px',
        maxWidth: '100%',
        '& > ul:last-of-type': {
          paddingBottom: theme.space.s40,
        },
        [mqMin.tabletLandscape]: {
          flex: '0 1 100%',
          width: '210px',
          maxWidth: '210px',
        },
      },
      isGroupSplit && {
        [mqMin.tabletLandscape]: {
          marginTop: theme.space.s24,
        },
      },
    ]}
    // eslint-disable-next-line react/jsx-sort-props
    className={className}
    data-testid="navDropdownMenuGroup"
  >
    {!link.shouldHideGroupTitle && (
      <StyledNavLabel
        aria-level={3}
        as="h3"
        dataElementPageLocation={isSuperNav ? 'superNav' : 'siteNav'}
        id={`navDropdownMenuGroupLabel-${link.entityId}-${index + 1}`}
        role="heading"
        type={isSuperNav ? 'superNav' : 'siteNav'}
        variant="interactiveXS400"
      >
        {link.title}
        <hr />
      </StyledNavLabel>
    )}
    <ul
      aria-labelledby={`navDropdownMenuGroupLabel-${link.entityId}-${index + 1}`}
      css={{
        label: 'navDropdownMenuGroupUL',
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      {link.linkGroups?.map(itemLinkGroups => {
        if (itemLinkGroups && !!itemLinkGroups.length) {
          return itemLinkGroups?.map(subItemLink =>
            renderDropdownLink({
              link: subItemLink,
              index,
              isSuperNav,
              isDropdownOpen,
              product,
            }),
          );
        }

        const itemLinkGroup = itemLinkGroups as Lucid.Drupal.LinkInterface;
        return renderDropdownLink({
          link: itemLinkGroup,
          index,
          isSuperNav,
          isDropdownOpen,
          product,
        });
      })}
    </ul>
  </Box>
);

export { NavDropdownMenuGroup };
export default NavDropdownMenuGroup;
